<template>
  <v-main class="pa-0 fill-height">
    <div id="mapa">
      <gmap-map
        ref="gmap"
        :center="map.center"
        :zoom="map.zoom"
        style="width: 100%; height: 100%"
        :options="map.options"
      >
        <GmapCluster
          :ignoreHidden="false"
          :averageCenter="true"
          :gridSize="60"
          :zoomOnClick="true"
          :clickable="true"
          :animation="2"
        >
          <gmap-marker
            v-for="m in markersRendered"
            :key="`mk-${m.id}`"
            :position="{ lat: m.lat, lng: m.lng }"
            :icon="bus"
            :options="{ optimized: true }"
            :draggable="false"
            @mouseover="openWindow(m)"
            @mouseout="closeWindow(m)"
          >
            <gmap-info-window :opened="m.dialog">
              <div class="vehicleModal">
                <b>Número económico:</b> {{ m.numero_economico || '-' }}<br />
                <b>Ramal:</b> {{ m.ramal || '-' }}<br />
              </div>
            </gmap-info-window>
          </gmap-marker>
        </GmapCluster>

        <!-- PARADAS -->
        <GmapCluster :ignoreHidden="false" :position="map.center" :zoomOnClick="true" :clickable="true" :animation="2">
          <div v-for="stop in stops" :key="`stop-${stop.idparada_externo}`">
            <gmap-marker
              v-if="stop.visible"
              :position="stop.coor"
              :icon="icon"
              :draggable="false"
              @mouseover="openWindow(stop)"
              @mouseout="closeWindow(stop)"
            >
              <gmap-info-window :opened="stop.dialog">
                <div class="dialog">
                  {{ stop.nombre_parada }}
                </div>
              </gmap-info-window>
            </gmap-marker>
          </div>
        </GmapCluster>
        <gmap-polyline
          v-for="pl in polylines"
          :key="`pl-${pl.idramal}`"
          :path="pl.path"
          :options="{
            strokeColor: pl.color,
            strokeOpacity: 0.95,
            strokeWeight: 4,
            clickable: true,
            geodesic: false
          }"
        />
      </gmap-map>

      <div id="filterBox" style="margin-left:10px">
        <v-card class="filterCard elevation-6 pa-3" max-width="800">
          <v-row no-gutters>
            <v-col cols="12" sm="3" class="pr-sm-3">
              <v-autocomplete
                v-model="selectedClienteId"
                :items="clientItems"
                item-text="text"
                item-value="value"
                label="Clientes"
                dense
                hide-details
                clearable
                :loading="loadingClients"
                prepend-inner-icon="mdi-account-multiple"
                @change="onClienteChange"
              />
            </v-col>

            <v-col cols="12" sm="4" class="pr-sm-4 mt-4 mt-sm-0">
              <v-autocomplete
                v-model="selectedRamalIds"
                :items="ramalItems"
                item-text="text"
                item-value="value"
                label="Ramal (requerido)"
                dense
                hide-details
                clearable
                multiple
                chips
                small-chips
                :disabled="!Number.isFinite(idclienteEff)"
                :loading="loadingRamales"
                prepend-inner-icon="mdi-sign-direction"
              />
            </v-col>

            <v-col cols="12" sm="2" class="mt-2 mt-sm-0 d-flex justify-center">
              <v-btn
                small outlined elevation="0"
                color="pinkKonect"
                :loading="loading"
                :disabled="!isFiltroValido"
                @click="onConsultar"
              >
                Consultar
                <v-icon right>mdi-magnify</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="12" sm="2" class="mt-2 mt-sm-0 d-flex justify-center">
              <v-btn
                small
                outlined
                elevation="0"
                color="grey darken-1"
                @click="onLimpiar"
              >
                Limpiar
                <v-icon right>mdi-close</v-icon>
              </v-btn>  
            </v-col>
          </v-row>
        </v-card>
      </div>

      <v-snackbar v-model="success" color="success" :timeout="2200" bottom right>
        {{ msgSuccess }}
      </v-snackbar>
      <v-snackbar v-model="error" color="error" :timeout="2500" bottom right style="white-space: pre-line">
        {{ codeError || 'Error al consultar' }}
      </v-snackbar>
      <v-dialog v-model="loading" hide-overlay persistent width="300">
        <v-card color="primary" dark>
          <v-card-text>
            Cargando
            <v-progress-linear indeterminate color="white" class="mb-0" />
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </v-main>
</template>

<script>
import _ from 'lodash'
import services from '@/utils/services'
import { gmapApi } from 'vue2-google-maps'

const PALETTE = [
  '#4f46e5', '#22c55e', '#f59e0b', '#8b5cf6', '#ef4444',
  '#06b6d4', '#ec4899', '#a16207', '#16a34a', '#0ea5e9'
]

export default {
  name: 'MapaClientesRamalesLikeSnippet',

  data: () => ({
    loading: false,
    loadingClients: false,
    loadingRamales: false,
    success: false,
    msgSuccess: '',
    error: false,
    codeError: '',

    selectedClienteId: null,
    clientItems: [],
    selectedRamalIds: [],
    ramalItems: [],

    trackingTimer: null,
    trackingBusy: false,        
    lastUpdateTracking: '',

    polylines: [],
    markers: [],                 
    markersIndex: Object.create(null), 

    icon: { url: require('@/assets/marker.png') },
    bus: { url: require('@/assets/busMarker.png'), scaledSize: null },

    map: {
      center: { lat: 25.689665, lng: -100.310619 },
      zoom: 13,
      options: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        disableDefaultUi: false
      }
    },

    stops: [],
  }),

  computed: {
    google: gmapApi,
    idclienteEff() {
      const n = Number(this.selectedClienteId)
      return Number.isFinite(n) && n > 0 ? n : NaN
    },
    selectedRamalNames() {
      const map = new Map(this.ramalItems.map(r => [r.value, r.text]))
      return (this.selectedRamalIds || [])
        .map(id => (map.get(id) || '').toLowerCase().trim())
        .filter(Boolean)
    },
    isFiltroValido() {
      return Number.isFinite(this.idclienteEff)
        && Array.isArray(this.selectedRamalIds)
        && this.selectedRamalIds.length > 0
    },

    markersRendered() {
      return this.markers.filter(m => m.visible === true)
    },
  },

  watch: {
    isFiltroValido(val) {
      if (!val) {
        this.stopPollingTracking()
        this.markers = []
        this.markersIndex = Object.create(null)
      }
    },
    selectedRamalIds() {
      if (this.trackingTimer) {
        this.fetchTrackingGeo().catch(() => {})
      }
    },
  },

  mounted() {
    this.$refs.gmap.$mapPromise.then((map) => {
      const filter = document.getElementById('filterBox')
      filter.index = 1
      map.controls[this.google.maps.ControlPosition.LEFT_TOP].push(filter)
    })

    const setBusSize = () => {
      try {
        const factor = 0.75
        const w = 48 * factor
        const h = 64 * factor
        if (this.google && this.google.maps) {
          this.bus.scaledSize = new this.google.maps.Size(w, h)
          this.bus.anchor = new this.google.maps.Point(w / 2, h)
        } else if (window.google?.maps) {
          this.bus.scaledSize = new window.google.maps.Size(w, h)
          this.bus.anchor = new window.google.maps.Point(w / 2, h)
        }
      } catch (_) {}
    }
    if (typeof this.$gmapApiPromiseLazy === 'function') {
      this.$gmapApiPromiseLazy().then(setBusSize)
    } else {
      setTimeout(setBusSize, 400)
    }

    this.loadClients()
  },

  beforeDestroy() {
    this.stopPollingTracking()
  },

  methods: {
    digitsOnly(v) { return String(v ?? '').replace(/\D+/g, '') },

    toPathFromLineString(line) {
      const coords = Array.isArray(line?.coordinates) ? line.coordinates : []
      return coords.map(pair => {
        if (!Array.isArray(pair) || pair.length < 2) return null
        const lng = Number(pair[0]); const lat = Number(pair[1])
        return (Number.isFinite(lat) && Number.isFinite(lng)) ? { lat, lng } : null
      }).filter(Boolean)
    },

    onLimpiar() {
      this.selectedRamalIds = []
      this.polylines = []
      this.stops = []
      this.markers = []
      this.markersIndex = Object.create(null)
      this.stopPollingTracking()
      this.msgSuccess = 'Filtros limpiados'
      this.success = true
    },

    latLngFromPosicion(obj) {
      let lat = Number(obj?.latitud ?? NaN)
      let lng = Number(obj?.longitud ?? NaN)

      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        const inLat = Math.abs(lat) <= 90
        const inLng = Math.abs(lng) <= 180
        if ((!inLat && Math.abs(lng) <= 90) || (inLat && !inLng && Math.abs(lat) <= 180)) {
          [lat, lng] = [lng, lat]
        }
        if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng }
        return null
      }

      const coords = obj?.posicion?.coordinates
      if (Array.isArray(coords) && coords.length >= 2) {
        const L = Number(coords[1]); const G = Number(coords[0])
        if (Number.isFinite(L) && Math.abs(L) <= 90 && Number.isFinite(G) && Math.abs(G) <= 180) {
          return { lat: L, lng: G }
        }
      }

      const wkt = obj?.posicion
      if (typeof wkt === 'string') {
        const m = wkt.match(/POINT\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/i)
        if (m) {
          const G = Number(m[1]); const L = Number(m[2])
          if (Number.isFinite(L) && Math.abs(L) <= 90 && Number.isFinite(G) && Math.abs(G) <= 180) {
            return { lat: L, lng: G }
          }
        }
      }
      return null
    },

    pickColor(idramal, idx) {
      if (Number.isFinite(idramal)) return PALETTE[Math.abs(Number(idramal)) % PALETTE.length]
      return PALETTE[idx % PALETTE.length]
    },

    fitToOverlays(padding = 60) {
      const hasPolys = this.polylines.length > 0
      const hasMarks = this.markers.length > 0
      if ((!hasPolys && !hasMarks) || !this.$refs.gmap || !window.google) return
      const map = this.$refs.gmap.$mapObject
      const bounds = new window.google.maps.LatLngBounds()
      if (hasPolys) this.polylines.forEach(pl => pl.path.forEach(pt => bounds.extend(pt)))
      if (hasMarks) this.markers.forEach(m => bounds.extend({ lat: m.lat, lng: m.lng }))
      if (!bounds.isEmpty()) map.fitBounds(bounds, padding)
    },

    onClienteChange() {
      this.selectedRamalIds = []
      this.ramalItems = []
      this.polylines = []
      this.markers = []
      this.markersIndex = Object.create(null)
      this.stopPollingTracking()
      if (Number.isFinite(this.idclienteEff)) this.loadRamales()
    },

    async loadClients() {
      this.loadingClients = true
      try {
        const res = await services.ws('get', 'clients')
        const arr = Array.isArray(res?.data) ? res.data : []
        this.clientItems = arr
          .map(c => ({ text: c.nombre_comercial, value: Number(c.idcliente), ext: c.idcliente_externo }))
          .filter(o => Number.isFinite(o.value) && o.value > 0)
          .sort((a,b) => a.text.localeCompare(b.text, 'es'))
      } catch (e) {
        this.error = true
        this.codeError = _.get(e, 'response.data.message', 'No se pudieron cargar los clientes')
      } finally {
        this.loadingClients = false
      }
    },

    async loadRamales() {
      const idcliente = this.idclienteEff
      if (!Number.isFinite(idcliente)) return
      this.loadingRamales = true
      try {
        const baseUrl = _.get(services, 'routes.listRam', null)
        const resp = baseUrl
          ? await this.axios.get(`${baseUrl}${idcliente}`)
          : await services.ws('get', `listRam/${idcliente}`)

        const data = Array.isArray(resp?.data) ? resp.data
                    : (Array.isArray(resp?.data?.data) ? resp.data.data : [])

        this.ramalItems = (data || [])
          .map(r => ({
            text: r.nombre_ramal || r.nombre || `Ramal ${r.idramal ?? ''}`,
            value: Number(r.idramal ?? r.id_ramal ?? r.id) || null,
          }))
          .filter(x => Number.isFinite(x.value))
          .sort((a,b) => a.text.localeCompare(b.text, 'es'))
      } catch (e) {
        this.ramalItems = []
        this.error = true
        this.codeError = _.get(e, 'response.data.message', 'No se pudieron cargar los ramales')
      } finally {
        this.loadingRamales = false
      }
    },

    toLocalTime(iso) {
      try {
        if (!iso) return null
        const d = new Date(iso)
        return d.toLocaleString('es-MX', { hour12: false })
      } catch (_) {
        return null
      }
    },

    async fetchTrackingGeo() {
      if (!this.isFiltroValido) {
        this.markers = []
        this.markersIndex = Object.create(null)
        return
      }
      if (this.trackingBusy) return
      this.trackingBusy = true

      try {
        const resp = await services.ws('get', 'trackingGeo') 
        const raw = Array.isArray(resp?.data)
          ? resp.data
          : (Array.isArray(resp?.data?.data) ? resp.data.data : [])

        const sel = new Set(this.selectedRamalNames) 

        // marcar existentes como no vistos
        Object.values(this.markersIndex).forEach(m => { m._seen = false })

        ;(raw || []).forEach((v, idx) => {
          const coords = _.get(v, 'posicion.coordinates', [])
          const lng = Number(coords[0]); const lat = Number(coords[1])
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return

          const ramalNorm = String(v.ramal || '').toLowerCase().trim()

          // coincidencia estricta; si quieres tolerante usa .some/includes
          if (sel.size && !sel.has(ramalNorm)) return

          const eco = String(v.numeroEconomico || '').trim()
          const id = eco || `veh-${idx}`

          let obj = this.markersIndex[id]
          if (!obj) {
            obj = {
              id,
              numero_economico: eco,
              operador: v.operador || null,
              ramal: v.ramal || null,
              velocidad: Number(v.velocidad ?? NaN),
              direccion: Number(v.direccion ?? NaN),
              estado: Number(v.estado ?? NaN),
              ultimaActualizacion: v.ultimaActualizacion || null,
              ultimaActualizacionLocal: this.toLocalTime(v.ultimaActualizacion),
              lat, lng,
              dialog: false,
              visible: true,
              _seen: true,
            }
            this.$set(this.markersIndex, id, obj)
            this.markers.push(obj)
          } else {
            obj.lat = lat
            obj.lng = lng
            obj.ramal = v.ramal || null
            obj.numero_economico = eco
            obj.velocidad = Number(v.velocidad ?? NaN)
            obj.direccion = Number(v.direccion ?? NaN)
            obj.estado = Number(v.estado ?? NaN)
            obj.ultimaActualizacion = v.ultimaActualizacion || null
            obj.ultimaActualizacionLocal = this.toLocalTime(v.ultimaActualizacion)
            obj.visible = true
            obj._seen = true
          }
        })
        this.markers.forEach(m => {
          if (!m._seen) m.visible = false
          delete m._seen
        })

        this.lastUpdateTracking = this.toLocalTime(new Date().toISOString())
      } catch (_) {
      } finally {
        this.trackingBusy = false
      }
    },

    stopPollingTracking() {
      if (this.trackingTimer) {
        clearInterval(this.trackingTimer)
        this.trackingTimer = null
      }
    },

    startPollingTracking(intervalMs = 15000) {
      this.stopPollingTracking()
      this.fetchTrackingGeo()
      this.trackingTimer = setInterval(() => {
        this.fetchTrackingGeo()
      }, intervalMs)
    },

    async fetchGeoRutas({ idcliente, idramal }) {
      if (!Array.isArray(idramal) || idramal.length === 0) {
        this.polylines = []
        this.stops = []
        return
      }
      const baseUrl = _.get(services, 'routes.geoRuta', null)
      const payload = { idcliente, idramal }
      const resp = baseUrl
        ? await this.axios.post(baseUrl, payload)
        : await services.ws('post', 'geoRuta', payload)

      const list = Array.isArray(resp?.data) ? resp.data
                  : (Array.isArray(resp?.data?.data) ? resp.data.data : (resp?.data ? [resp.data] : []))

      const pls = []
      const stopsArr = []

      ;(list || []).forEach((it, idx) => {
        const idramalVal = Number(_.get(it, 'idramal', NaN))
        const line = _.get(it, 'ruta.georuta') || _.get(it, 'georuta') || _.get(it, 'ruta')
        if (line?.type !== 'LineString') return
        const path = this.toPathFromLineString(line)
        if (path.length < 2) return
        let color = _.get(it, 'colorHex', null)
        color = typeof color === 'string' && color.length
          ? (color.startsWith('#') ? color : `#${color}`)
          : this.pickColor(idramalVal, idx)
        pls.push({ idramal: idramalVal, path, color })

        const paradas = _.get(it, 'ruta.paradas', [])
        if (Array.isArray(paradas)) {
          paradas.forEach(parada => {
            if (!parada?.coordenadas?.coordinates) return
            stopsArr.push({
              idparada_externo: parada.idparada_externo,
              nombre_parada: parada.nombre_parada,
              visible: true,
              dialog: false,
              coor: {
                lat: parada.coordenadas.coordinates[1],
                lng: parada.coordenadas.coordinates[0]
              }
            })
          })
        }
      })
      this.polylines = pls
      this.stops = stopsArr
    },
    async onConsultar() {
      if (!this.isFiltroValido) return

      this.loading = true
      this.error = false
      this.stopPollingTracking()

      try {
        const payload = {
          idcliente: this.idclienteEff,
          idramal: this.selectedRamalIds,
        }

        await this.fetchGeoRutas(payload)   
        await this.fetchTrackingGeo()       

        this.fitToOverlays()
        this.success = true
        this.msgSuccess = 'Datos obtenidos con éxito'

        this.startPollingTracking(15000)    
      } catch (e) {
        this.error = true
        this.codeError = _.get(e, 'response.data.message', _.get(e, 'message', 'Error al consultar'))
      } finally {
        this.loading = false
      }
    },

    openWindow(m) { m.dialog = true },
    closeWindow(m) { m.dialog = false },
  },
}
</script>

<style scoped>
.vehicleModal {
  align-content: center !important;
}

.filterCard {
  margin-top: 10px;
  padding-top: 12px;
  padding-bottom: 6px; 
  min-height: 70px;    
  width: clamp(760px, 86vw, 1000px); 
}

html, body, #app, .v-application, .v-application--wrap {
  height: 100%;
  min-height: 100dvh;
  margin: 0;
  overflow: hidden;
}

#mapa {
  width: 100%;
  height: 100dvh;
  position: relative;
}

.v-main {
  padding: 0 !important;
}

#mapa .vue-map-container,
#mapa .gmap-container {
  position: absolute;
  inset: 0;
}
</style>
